import './i18n/config';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { router } from './App';
import { queryClient } from './lib/queryClient';
import { reloadForChunkError } from './lib/chunkReload';
import AuthInitializer from './components/AuthInitializer';
import AppToaster from './components/AppToaster';
import './index.css';

// Restore saved theme before first paint to avoid flash
const savedTheme = (() => { try { return localStorage.getItem('udb_theme'); } catch { return null; } })();
if (savedTheme) document.documentElement.setAttribute('data-theme', savedTheme);

// Restore saved accent color before first paint to avoid flash
const savedAccent = (() => {
  try { return localStorage.getItem('udb_accent'); } catch { return null; }
})();
if (savedAccent && savedAccent !== 'flame') {
  document.documentElement.setAttribute('data-accent', savedAccent);
}

// Stale chunk after a deploy (see lib/chunkReload.ts). Catches any dynamic
// import or CSS preload that isn't wrapped by lazyWithReload. Not
// preventDefault()-ed: the rejection still reaches lazyWithReload, which sees
// the reload already under way and keeps the Suspense fallback up.
window.addEventListener('vite:preloadError', (event) => {
  reloadForChunkError((event as Event & { payload?: unknown }).payload);
});

const root = document.getElementById('root');
if (!root) throw new Error('Root element #root not found');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthInitializer>
        <RouterProvider router={router} />
      </AuthInitializer>
      <AppToaster />
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </StrictMode>,
);
